import { useState } from "react";
import { Circle, Group, Label, Tag, Text } from "react-konva";
import { useAppSelector } from "@store/hooks";
import {
  selectCanvasDiagnostics,
  selectViewedRectangles,
} from "@store/selectors";

const BADGE_RADIUS = 9;
const BADGE_OFFSET = 4;

interface BadgeProps {
  rectX: number;
  rectY: number;
  rectWidth: number;
  messages: string[];
}

const CanvasDiagnosticBadge = ({
  rectX,
  rectY,
  rectWidth,
  messages,
}: BadgeProps) => {
  const [hovered, setHovered] = useState(false);
  const badgeX = rectX + rectWidth + BADGE_OFFSET + BADGE_RADIUS;
  const badgeY = rectY - BADGE_OFFSET - BADGE_RADIUS;

  return (
    <Group>
      <Circle
        x={badgeX}
        y={badgeY}
        radius={BADGE_RADIUS}
        fill="#fff"
        stroke="#000"
        strokeWidth={1.5}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />
      <Text
        text="i"
        x={badgeX - 2}
        y={badgeY - 6}
        fontSize={12}
        fontStyle="italic bold"
        fill="#000"
        listening={false}
      />
      {hovered && (
        <Label
          x={badgeX + BADGE_RADIUS + 4}
          y={badgeY - BADGE_RADIUS}
          listening={false}
        >
          <Tag fill="#212121" cornerRadius={4} />
          <Text
            text={messages.map((m) => `• ${m}`).join("\n")}
            fontSize={11}
            fill="#fff"
            padding={6}
            lineHeight={1.4}
          />
        </Label>
      )}
    </Group>
  );
};

/**
 * Mirror of the reader's DiagnosticBadge, drawn in Konva. Renders a
 * small "i" badge at the top-right of each non-matched rectangle; on
 * hover, surfaces the diagnostic bullets in a Konva-Label tooltip.
 * Mounted after the rectangle map in Canvas so the badges sit on top.
 */
const CanvasDiagnosticLayer = () => {
  const diagnostics = useAppSelector(selectCanvasDiagnostics);
  const rectangles = useAppSelector(selectViewedRectangles);
  if (!diagnostics.active) return null;

  return (
    <Group>
      {rectangles.map((rect, idx) => {
        const status = diagnostics.rectStatusByIdx[idx];
        if (!status || status === "matched") return null;
        const messages = diagnostics.messagesByIdx[idx];
        if (!messages || messages.length === 0) return null;
        return (
          <CanvasDiagnosticBadge
            key={`diag-${idx}`}
            rectX={rect.x}
            rectY={rect.y}
            rectWidth={rect.width}
            messages={messages}
          />
        );
      })}
    </Group>
  );
};

export default CanvasDiagnosticLayer;
